import { CurrencyText, InlineEdit, Row, CurrencyField } from '@nile/components';
import { DisabledTag } from '@nile/mangifera/src/Pebbles/TicketContainer';
import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { SVGIcon } from '@superops/super-icons';
import { PositiveNumberField } from '@superops/ui-design-system';
import { ReactCloneTippy } from '@nile/client_management/src/Contracts/Components/ReactCloneTippy';
import { isEmpty, isEqual } from 'lodash';
import { ClientContractStatus } from '@nile/types';
import { ServiceCatalogItem } from '../../types';

function PriceCell(props) {
  const { cell, params } = props;
  const allProps = params || cell.column;
  const { editable, id, actions, checkForContractStatus } = allProps;
  const row: ServiceCatalogItem & { contractStatus: ClientContractStatus } =
    cell?.row?.original?.serviceCatalogItem || cell.row.original;

  let editableForItemType = false;

  let displayVal = '';
  switch (id) {
    case 'costPrice':
      displayVal = cell?.value?.details[0]?.value;

      if (
        row?.itemType === 'PRODUCT' ||
        (row?.itemType === 'SERVICE' &&
          row?.serviceTypeItem?.offeringType === 'QUANTITY')
      ) {
        editableForItemType = !!editable;
      }
      break;
    case 'afterHoursUnitPrice':
      displayVal = cell?.value?.details[0]?.afterHoursValue;
      if (
        row?.itemType === 'SERVICE' &&
        row?.serviceTypeItem?.offeringType === 'HOURS'
      ) {
        editableForItemType = !!editable;
      }
      break;
    default:
      displayVal = cell?.value?.details[0]?.value;
      editableForItemType = !!editable;
      if (cell.row.original?.contract) {
        editableForItemType =
          !!editable &&
          !!cell.row.original?.contract?.billableContract
            ?.sellingPriceOverridden &&
          cell.row.original?.contract?.billableContract
            ?.sellingPriceCalculationType !== 'DYNAMIC';
      }
      break;
  }

  if (checkForContractStatus) {
    editableForItemType =
      !!editable &&
      cell.row.original?.contract?.billableContract
        ?.sellingPriceCalculationType !== 'DYNAMIC' &&
      !!cell.row.original?.contract?.billableContract?.sellingPriceOverridden;
  }

  if (
    !!row?.integrationState?.pax8 ||
    row?.serviceTypeItem?.defaultUsageType === 'LATE_FEE' ||
    row?.serviceTypeItem?.offeringType === 'BLOCK_MONEY' ||
    row?.status === 'INACTIVE' ||
    row?.contractStatus === 'INACTIVE'
  ) {
    editableForItemType = false;
  }

  const [value, setValue] = useState<string>(displayVal);

  const isDynamic =
    cell.row.original?.contract?.billableContract
      ?.sellingPriceCalculationType === 'DYNAMIC';

  useEffect(() => {
    if (!isEqual(displayVal, value)) setValue(displayVal);
  }, [displayVal]);

  return (
    <div className="fx fg1">
      <InlineEdit
        showProgress
        saveOnEnter
        hideActionButtons
        editable={editableForItemType}
        defaultValue={value}
        editView={({
          updateFieldState
        }: {
          updateFieldState: (text: string) => void;
        }) => {
          return (
            <CurrencyField
              type="number"
              value={value}
              label={id}
              labelHidden
              autoComplete="off"
              autoFocus
              min={0}
              onChange={(text) => {
                setValue(text);
                updateFieldState(text);
              }}
            />
          );
        }}
        readView={() => {
          return (
            <div
              className={`ellipsis ${
                editableForItemType ? 'editable-cell' : ''
              } p8`}
            >
              <div className="fg1 fx actr g5">
                {!isEmpty(value) ? (
                  <Row className="actr" gap={5}>
                    <CurrencyText value={value} />
                    {isDynamic && (
                      <ReactCloneTippy text="common.dynamicPrice" distance={20}>
                        <SVGIcon name="Bolt" />
                      </ReactCloneTippy>
                    )}
                  </Row>
                ) : (
                  <DisabledTag>
                    <FormattedMessage id="app.noData.message" />
                  </DisabledTag>
                )}
                {editableForItemType && (
                  <span className="fx mlauto action-button">
                    <SVGIcon name="Edit" />
                  </span>
                )}
              </div>
            </div>
          );
        }}
        onConfirm={(val, callback) => {
          if (!val?.trim()) setValue(displayVal);
          else setValue(val.trim());
          const nameKey = id === 'afterHoursUnitPrice' ? 'sellingPrice' : id;
          const valueKey =
            id === 'afterHoursUnitPrice' ? 'afterHoursValue' : 'value';

          if (val && actions.save) {
            actions
              .save(
                row?.itemId
                  ? {
                      itemId: row?.itemId,
                      [nameKey]: {
                        ...cell?.value,
                        model: 'PER_UNIT',
                        details: [
                          {
                            ...cell?.value?.details[0],
                            [valueKey]: val
                          }
                        ]
                      }
                    }
                  : {
                      contractId: row?.contract?.contractId,
                      [id]: {
                        model: 'PER_UNIT',
                        details: [
                          {
                            ...cell?.value?.details[0],
                            value: val
                          }
                        ]
                      }
                    },
                id
              )
              .then(() => {
                callback('success');
              })
              .catch(() => {
                setValue(displayVal);
                callback('error');
              });
          } else {
            callback('success');
          }
        }}
      />
    </div>
  );
}

export default PriceCell;
